<template>
  <!-- Loading State -->
  <div
    class="col-span-6 sm:col-span-3 xl:col-span-2 flex flex-col justify-end items-center"
    v-if="loading"
  >
    <LoadingIcon icon="three-dots" class="w-8 h-8" />
  </div>

  <form class="validate-form" @submit.prevent="handleSubmit" v-else>
    <div>
      <div class="intro-y flex flex-col sm:flex-row items-center mt-8">
        <MapPinIcon />
        <h2 class="text-lg font-medium mr-auto">
          {{ mode === 'edit' ? $t('branch.edit') : $t('branch.add') }}
        </h2>
        <div class="w-full sm:w-auto flex mt-4 sm:mt-0">
          <button type="submit" class="btn btn-primary shadow-md flex items-center">
            <SaveIcon class="w-4 h-4 mr-2" /> {{ $t('common.save') }}
          </button>
          <router-link
            :to="{ name: 'side-menu-branches' }"
            class="btn shadow-md flex items-center"
          >
            <ArrowLeftCircleIcon class="w-4 h-4 mr-2" /> {{ $t('common.back') }}
          </router-link>
        </div>
      </div>

      <div class="pos intro-y grid grid-cols-12 gap-5 mt-5">
        <div class="col-span-12">
          <div class="intro-y box p-5">
            <!-- NAME -->
            <div class="mt-3">
              <label for="name" class="form-label w-full flex flex-col sm:flex-row">
                {{ $t('common.name') }}
                <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                  {{ $t('form.required') }}, {{ $t('form.constraint.gte2ch') }}
                </span>
              </label>
              <input
                id="name"
                v-model.trim="validate.name.$model"
                type="text"
                class="intro-y form-control py-3 px-4 box pr-10"
                :class="{ 'border-theme-6': validate.name.$error }"
                placeholder="My Branch"
              />
              <div v-if="validate.name.$error" class="text-theme-6 mt-2">
                <div v-for="(error, index) in validate.name.$errors" :key="index">
                  {{ $t(error.$message) }}
                </div>
              </div>
            </div>

            <!-- TABS (Languages) -->
            <div class="post intro-y overflow-hidden box mt-5">
              <div
                class="post__tabs nav nav-tabs flex-col sm:flex-row bg-gray-300 dark:bg-dark-2 text-gray-600"
                role="tablist"
              >
                <a
                  id="sk-tab"
                  data-toggle="tab"
                  data-target="#sk"
                  href="javascript:;"
                  class="flex-1 py-2 rounded-md text-center active"
                  role="tab"
                  aria-selected="true"
                >
                  <country-flag country="sk" size="small" />
                  {{ $t('common.language.sk_SK') }}
                </a>
                <a
                  id="en-tab"
                  data-toggle="tab"
                  data-target="#en"
                  href="javascript:;"
                  class="flex-1 py-2 rounded-md text-center"
                  role="tab"
                  aria-selected="false"
                >
                  <country-flag country="gb" size="small" />
                  {{ $t('common.language.en_UK') }}
                </a>
                <a
                  id="cs-tab"
                  data-toggle="tab"
                  data-target="#cs"
                  href="javascript:;"
                  class="flex-1 py-2 rounded-md text-center"
                  role="tab"
                  aria-selected="false"
                >
                  <country-flag country="cz" size="small" />
                  {{ $t('common.language.cs_CZ') }}
                </a>
                <a
                  id="de-tab"
                  data-toggle="tab"
                  data-target="#de"
                  href="javascript:;"
                  class="flex-1 py-2 rounded-md text-center"
                  role="tab"
                  aria-selected="false"
                >
                  <country-flag country="de" size="small" />
                  {{ $t('common.language.de_DE') }}
                </a>
                <a
                  id="pl-tab"
                  data-toggle="tab"
                  data-target="#pl"
                  href="javascript:;"
                  class="flex-1 py-2 rounded-md text-center"
                  role="tab"
                  aria-selected="false"
                >
                  <country-flag country="pl" size="small" />
                  {{ $t('common.language.pl_PL') }}
                </a>
              </div>

              <div class="tab-content">
                <!-- SK -->
                <div
                  class="tab-pane active"
                  id="sk"
                  role="tabpanel"
                  aria-labelledby="sk-tab"
                >
                  <div class="mt-3">
                    <label class="form-label mt-3">{{ $t('common.description') }}</label>
                    <textarea
                      class="intro-y form-control py-3 px-4 box"
                      v-model="descriptionSk"
                    ></textarea>
                  </div>
                </div>
                <!-- EN -->
                <div class="tab-pane" id="en" role="tabpanel" aria-labelledby="en-tab">
                  <div class="mt-3">
                    <label class="form-label mt-3">{{ $t('common.description') }}</label>
                    <textarea
                      class="intro-y form-control py-3 px-4 box"
                      v-model="descriptionEn"
                    ></textarea>
                  </div>
                </div>
                <!-- CS -->
                <div class="tab-pane" id="cs" role="tabpanel" aria-labelledby="cs-tab">
                  <div class="mt-3">
                    <label class="form-label mt-3">{{ $t('common.description') }}</label>
                    <textarea
                      class="intro-y form-control py-3 px-4 box"
                      v-model="descriptionCs"
                    ></textarea>
                  </div>
                </div>
                <!-- DE -->
                <div class="tab-pane" id="de" role="tabpanel" aria-labelledby="de-tab">
                  <div class="mt-3">
                    <label class="form-label mt-3">{{ $t('common.description') }}</label>
                    <textarea
                      class="intro-y form-control py-3 px-4 box"
                      v-model="descriptionDe"
                    ></textarea>
                  </div>
                </div>
                <!-- PL -->
                <div class="tab-pane" id="pl" role="tabpanel" aria-labelledby="pl-tab">
                  <div class="mt-3">
                    <label class="form-label mt-3">{{ $t('common.description') }}</label>
                    <textarea
                      class="intro-y form-control py-3 px-4 box"
                      v-model="descriptionPl"
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>

            <!-- IMAGES -->
            <div class="mt-3" :class="{ 'border-theme-6': validate.image.$error }">
              <label class="form-label w-full flex flex-col sm:flex-row">
                {{ $t('common.branchImg') }}
                <span class="sm:ml-auto text-xs text-gray-600">{{
                  $t('form.required')
                }}</span>
              </label>
              <ImageUploader
                ref="imageUploaderRef"
                :aspectRatio="1 / 1"
                :defaultImage="initialData?.image"
              />
              <div v-if="validate.image.$error" class="text-theme-6 mt-2">
                {{ $t('form.validation.imageRequired') }}
              </div>
            </div>

            <!-- COLOR -->
            <div class="mt-3">
              <label class="form-label">{{ $t('common.eventColor') }}</label>
              <Compact v-model="selectedColor" />
            </div>

            <!-- CONTACT -->
            <div class="mt-3">
              <label class="form-label">{{ $t('common.phoneNumber') }}</label>
              <input
                v-model.trim="validate.phoneNumber.$model"
                type="text"
                class="intro-y form-control py-3 px-4 box"
                :class="{ 'border-theme-6': validate.phoneNumber.$error }"
                placeholder="+421..."
              />
              <div v-if="validate.phoneNumber.$error" class="text-theme-6 mt-2">
                {{ $t('form.constraint.phoneFormat') }}
              </div>
            </div>

            <div class="mt-3">
              <label class="form-label">Email</label>
              <input
                v-model.trim="validate.mail.$model"
                type="text"
                class="intro-y form-control py-3 px-4 box"
                :class="{ 'border-theme-6': validate.mail.$error }"
                placeholder="info@..."
              />
              <div v-if="validate.mail.$error" class="text-theme-6 mt-2">
                {{ $t('form.validation.email') }}
              </div>
            </div>

            <div class="mt-3">
              <label class="form-label">Web</label>
              <input
                v-model.trim="validate.web.$model"
                type="text"
                class="intro-y form-control py-3 px-4 box"
                :class="{ 'border-theme-6': validate.web.$error }"
                placeholder="https://..."
              />
              <div v-if="validate.web.$error" class="text-theme-6 mt-2">
                {{ $t('form.validation.url') }}
              </div>
            </div>

            <!-- CONFIG -->
            <div class="mt-3">
              <label class="form-label"
                >{{ $t('form.field.branchCancelLimit') }} (min)</label
              >
              <input
                v-model.trim="validate.cancelLimit.$model"
                type="number"
                class="intro-y form-control py-3 px-4 box"
                :class="{ 'border-theme-6': validate.cancelLimit.$error }"
              />
              <div v-if="validate.cancelLimit.$error" class="text-theme-6 mt-2">
                {{ $t('form.validation.minutes0') }}
              </div>
            </div>

            <div class="mt-3">
              <label class="form-label"
                >{{ $t('form.field.branchServiceInterval') }} (min)</label
              >
              <input
                v-model.trim="validate.serviceMinuteInterval.$model"
                type="number"
                class="intro-y form-control py-3 px-4 box"
                :class="{ 'border-theme-6': validate.serviceMinuteInterval.$error }"
              />
              <div v-if="validate.serviceMinuteInterval.$error" class="text-theme-6 mt-2">
                {{ $t('form.validation.minutes1') }}
              </div>
            </div>

            <!-- SELECTS -->
            <div class="mt-3"><PaymentTypeMultiselect v-model:payments="payments" /></div>
            <div class="mt-3">
              <BranchCategoryMultiselect
                v-model:selectedCategories="selectedCategories"
              />
            </div>
            <div class="mt-3">
              <ServiceMultiselect v-model:selectedServices="selectedServices" />
            </div>
            <div class="mt-3">
              <ResourceMultiselect v-model:selectedResources="selectedResources" />
            </div>
            <div class="mt-3">
              <EmployeeMultiselect v-model:selectedEmployees="selectedEmployees" />
            </div>

            <!-- ADDRESS -->
            <div class="intro-y box p-5 mt-5">
              <h3 class="font-medium text-base mb-3">{{ $t('common.address') }}</h3>
              <AddressForm v-model="validate.address.$model" ref="addressFormRef" />
            </div>

            <!-- OPEN HOURS -->
            <div class="intro-y box p-5 mt-5">
              <OpenHoursForm v-model:openHours="openHours" />
            </div>

            <!-- TOGGLES -->
            <div class="form-check flex-col items-start mt-3">
              <label for="visible" class="form-check-label ml-0 mb-2">{{
                $t('common.visibility')
              }}</label>
              <input
                id="visible"
                v-model="validate.visible.$model"
                class="form-check-switch"
                type="checkbox"
              />
            </div>
            <div class="form-check flex-col items-start mt-3">
              <label for="active" class="form-check-label ml-0 mb-2">{{
                $t('common.active')
              }}</label>
              <input
                id="active"
                v-model="validate.active.$model"
                class="form-check-switch"
                type="checkbox"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script setup>
import { ref, reactive, toRefs, toRaw, watch, onMounted } from 'vue'
import {
  required,
  minLength,
  helpers,
  email,
  url,
  minValue,
  integer
} from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import Toastify from 'toastify-js'
import { Compact } from '@ckpack/vue-color'

// Components
import PaymentTypeMultiselect from '../../components/payments/PaymentTypeMultiselect.vue'
import BranchCategoryMultiselect from '../../components/branches/BranchCategoryMultiselect.vue'
import ServiceMultiselect from '../../components/services/ServiceMultiselect.vue'
import ImageUploader from '@/components/cdn/ImageUploader.vue'
import WideImageUploader from '@/components/cdn/WideImageUploader.vue'
import EmployeeMultiselect from '@/components/employees/EmployeeMultiselect.vue'
import ResourceMultiselect from '@/components/resources/ResourceMultiselect.vue'
import AddressForm from '@/components/address-form/AddressForm.vue'
import OpenHoursForm from '@/components/branches/OpenHoursForm.vue'

// Props & Emits
const props = defineProps({
  loading: { type: Boolean, default: false },
  mode: { type: String, default: 'create' }, // 'create' or 'edit'
  initialData: { type: Object, default: () => ({}) }
})

const emit = defineEmits(['submit'])

// Refs
const addressFormRef = ref(null)
const imageUploaderRef = ref(null)

// Models
const selectedCategories = ref([])
const selectedServices = ref([])
const selectedResources = ref([])
const selectedEmployees = ref([])
const payments = ref([])
const selectedColor = ref({ hex: '#0e7fe1' })
const openHours = ref({
  openMonday: true,
  monday: '08:00-12:00',
  openTuesday: true,
  tuesday: '08:00-12:00',
  openWednesday: true,
  wednesday: '08:00-12:00',
  openThursday: true,
  thursday: '08:00-12:00',
  openFriday: true,
  friday: '08:00-12:00',
  openSaturday: true,
  saturday: '08:00-12:00',
  openSunday: true,
  sunday: '08:00-12:00'
})

// Descriptions
const descriptionSk = ref('')
const descriptionEn = ref('')
const descriptionCs = ref('')
const descriptionDe = ref('')
const descriptionPl = ref('')

// Form Data
const formData = reactive({
  name: '',
  phoneNumber: '',
  mail: '',
  web: '',
  cancelLimit: 60,
  serviceMinuteInterval: 15,
  visible: false,
  active: false,
  image: '',
  address: {
    street: '',
    streetNumber: '',
    city: '',
    zip: '',
    latitude: null,
    longitude: null,
    state: 'SK'
  }
})

// Validators
const phoneNumberValidator = helpers.regex(/^\+421[0-9]{9}$/)
const urlValidator = helpers.regex(/(https?:\/\/.*\.[a-z0-9]+)/i)
const rules = {
  name: {
    required: helpers.withMessage('form.validation.name.required', required),
    minLength: helpers.withMessage('form.validation.gte2ch', minLength(2))
  },
  phoneNumber: {
    phoneNumberValidator: helpers.withMessage(
      'form.validation.phoneNumberValidator',
      phoneNumberValidator
    )
  },
  mail: { email: helpers.withMessage('form.validation.email', email) },
  web: { url: helpers.withMessage('form.validation.url', url) },
  cancelLimit: {
    required: helpers.withMessage('form.validation.minutes0', required),
    integer,
    minValue: helpers.withMessage('form.validation.minutes0', minValue(0))
  },
  serviceMinuteInterval: {
    required: helpers.withMessage('form.validation.minutes1', required),
    integer,
    minValue: helpers.withMessage('form.validation.minutes1', minValue(1))
  },
  visible: {},
  active: {},
  image: {
    required: helpers.withMessage('form.validation.imageRequired', required),
    urlValidator: helpers.withMessage('form.validation.imageUrl', urlValidator)
  },
  address: { required: helpers.withMessage('form.validation.addressRequired', required) }
}
const validate = useVuelidate(rules, toRefs(formData))

// Populate Form Logic
const populateForm = (data) => {
  if (!data || Object.keys(data).length === 0) return

  formData.name = data.name || ''

  // Descriptions
  if (data.description) {
    descriptionSk.value = data.description.sk_SK || ''
    descriptionEn.value = data.description.en_US || ''
    descriptionCs.value = data.description.cs_CZ || ''
    descriptionDe.value = data.description.de_DE || ''
    descriptionPl.value = data.description.pl_PL || ''
  }

  formData.phoneNumber = data.phoneNumber || ''
  formData.mail = data.mail || ''
  formData.web = data.web || ''
  formData.cancelLimit = data.cancelLimit || 60
  formData.serviceMinuteInterval = data.serviceMinuteInterval || 15
  formData.active = !!data.active
  formData.visible = !!data.visible

  // Images
  formData.image = data.image || ''

  // Complex Objects
  if (data.color) selectedColor.value = { hex: data.color }
  if (data.address) Object.assign(formData.address, data.address)

  if (data.paymentTypes) payments.value.payments = data.paymentTypes
  if (data.categories) selectedCategories.value.selectedCategories = data.categories
  if (data.services) selectedServices.value.selectedServices = data.services
  if (data.resources) selectedResources.value.selectedResources = data.resources
  if (data.employees) selectedEmployees.value.selectedEmployees = data.employees
  if (data.openHours) openHours.value = data.openHours
}

// Watch initialData prop to populate form on load (for Edit mode)
watch(
  () => props.initialData,
  (newVal) => {
    populateForm(newVal)
  },
  { immediate: true, deep: true }
)

const handleSubmit = async () => {
  // 1. Get current values from uploaders
  const img = imageUploaderRef.value?.getValue()
  // Use uploaded val if exists, else keep existing (create: '', edit: 'url')
  formData.image = img !== null ? img : props.initialData.image || ''

  // 2. Parent Valid
  const isFormValid = await validate.value.$validate()

  // 3. Child Valid
  let isAddressValid = false
  if (addressFormRef.value && addressFormRef.value.validate) {
    isAddressValid = await addressFormRef.value.validate.$validate()
  }

  if (!isFormValid || !isAddressValid) {
    Toastify({
      node: cash('#failed-notification-content').clone().removeClass('hidden')[0], // Assuming these IDs exist in global layout or you handle toast differently
      duration: 5000,
      newWindow: true,
      close: true,
      gravity: 'top',
      position: 'right',
      stopOnFocus: true,
      text: 'Please correct validation errors' // Fallback text if DOM ID missing
    }).showToast()
    return
  }

  // 4. Construct Payload
  const payload = {
    name: formData.name,
    image: formData.image,
    color: toRaw(selectedColor.value).hex,
    phoneNumber: formData.phoneNumber,
    mail: formData.mail,
    web: formData.web,
    cancelLimit: formData.cancelLimit,
    serviceMinuteInterval: formData.serviceMinuteInterval,
    paymentTypes: toRaw(payments.value.payments) ?? [],
    categories: toRaw(selectedCategories.value.selectedCategories) ?? [],
    services: toRaw(selectedServices.value.selectedServices) ?? [],
    resources: toRaw(selectedResources.value.selectedResources) ?? [],
    employees: toRaw(selectedEmployees.value.selectedEmployees) ?? [],
    description: {
      sk_SK: descriptionSk.value,
      en_US: descriptionEn.value,
      cs_CZ: descriptionCs.value,
      de_DE: descriptionDe.value,
      pl_PL: descriptionPl.value
    },
    address: toRaw(formData.address),
    openHours: toRaw(openHours.value),
    active: formData.active,
    visible: formData.visible
  }

  emit('submit', payload)
}
</script>
